<script lang="ts">
  import { Link2, Link2Off } from '@lucide/svelte';
  import { PARAMETERS, PLOTS, normalizePlotSpec } from '../lib/registry';
  import type { ComparisonMode, Monitor, ParameterDefinition, PlotDefinition, PlotKind, PlotSpec, WorkspaceState } from '../lib/types';

  export let plot: PlotSpec;
  export let workspace: WorkspaceState;
  export let monitors: Monitor[];
  export let comparisonMode: ComparisonMode;
  export let effectiveMonitorId: string;
  export let parameter: ParameterDefinition;
  export let definition: PlotDefinition;
  export let onUpdate: (plot: PlotSpec) => void;

  function update(changes: Partial<PlotSpec>) {
    onUpdate(normalizePlotSpec({ ...plot, ...changes }));
  }

  function setKind(kind: PlotKind) {
    update({ kind });
  }

  function toggleLinked() {
    if (plot.linkedTime) {
      update({ linkedTime: false, rangeOverride: [...workspace.range], cursorOverride: workspace.cursor });
    } else {
      update({ linkedTime: true, rangeOverride: undefined, cursorOverride: undefined });
    }
  }

  function setLocation(monitorId: string) {
    update({ location: { mode: 'override', monitorId } });
  }

  function setOption(name: string, enabled: boolean) {
    update({ options: { ...plot.options, [name]: enabled } });
  }

</script>

<div class:panel-controls--with-location={comparisonMode === 'multiple-locations'} class="panel-controls">
  <label>Parameter
    <select value={plot.parameterId} on:change={(event) => update({ parameterId: (event.currentTarget as HTMLSelectElement).value })}>
      {#each PARAMETERS as item}
        <option value={item.id} disabled={!definition.compatible(item)}>{item.name}</option>
      {/each}
    </select>
  </label>
  <label>Plot type
    <select value={plot.kind} on:change={(event) => setKind((event.currentTarget as HTMLSelectElement).value as PlotKind)}>
      {#each PLOTS as item}
        <option value={item.kind} disabled={!item.compatible(parameter)}>{item.name}</option>
      {/each}
    </select>
  </label>
  {#if comparisonMode === 'multiple-locations'}
    <label>Monitoring location
      <select value={effectiveMonitorId} on:change={(event) => setLocation((event.currentTarget as HTMLSelectElement).value)}>
        {#each monitors as monitor (monitor.monitor_name_index)}
          <option value={monitor.monitor_name_index}>{monitor.primary_name}, {monitor.monitor_name_formatted} ({monitor.total_days} days)</option>
        {/each}
      </select>
    </label>
  {/if}
  <button class="button button--quiet" type="button" on:click={toggleLinked} title={plot.linkedTime ? 'Give this plot its own time selection' : 'Use the shared workspace time'}>
    {#if plot.linkedTime}<Link2 size={17} aria-hidden="true" /> Linked time{:else}<Link2Off size={17} aria-hidden="true" /> Link time{/if}
  </button>
</div>

{#if plot.kind === 'harmonic-spectrum' || plot.kind === 'harmonic-trend'}
<div class="option-row">
  {#if plot.kind === 'harmonic-spectrum'}
    <label><input type="checkbox" checked={plot.options.showFundamental} disabled={plot.options.showInterharmonics} on:change={(event) => setOption('showFundamental', (event.currentTarget as HTMLInputElement).checked)} /> Show fundamental</label>
    <label><input type="checkbox" checked={plot.options.perUnit} on:change={(event) => setOption('perUnit', (event.currentTarget as HTMLInputElement).checked)} /> Relative to fundamental</label>
    <label><input type="checkbox" checked={plot.options.showInterharmonics} on:change={(event) => setOption('showInterharmonics', (event.currentTarget as HTMLInputElement).checked)} /> Interharmonics</label>
  {:else if plot.kind === 'harmonic-trend'}
    <label><input type="checkbox" checked={plot.options.showFundamental} on:change={(event) => setOption('showFundamental', (event.currentTarget as HTMLInputElement).checked)} /> Show fundamental</label>
    <label><input type="checkbox" checked={plot.options.perUnit} on:change={(event) => setOption('perUnit', (event.currentTarget as HTMLInputElement).checked)} /> Relative to fundamental</label>
  {/if}
</div>
{/if}
